import type { CalendarDate } from "@internationalized/date";
import type { FocusState } from "./range";

type Tuple<T> = [T, T];

/**
 * Simplified date selection logic that works with the new FocusState enum
 */
export const useDateSelection =
  (
    selectedDates: Tuple<CalendarDate | undefined>,
    setSelectedDates: Tuple<(date?: CalendarDate) => void>,
    focusState: FocusState,
    endReference: React.RefObject<HTMLInputElement | null>,
  ) =>
  (date: CalendarDate) => {
    const isEndFocused = focusState === "end";
    // Android TalkBack (and some SRs) may not move DOM focus to the return input
    // after the departure date is chosen (programmatic focus can be ignored).
    // That leaves focusState stuck on "start", causing the next calendar tap
    // to overwrite the start date instead of setting the end date. To make the
    // component resilient, we treat the second selection as an end-date
    // selection whenever a start date exists, no end date is set, and the
    // chosen date is on/after the start (consistent with typical range pickers).
    const selectingImplicitEnd =
      focusState === "none" &&
      !selectedDates[1] &&
      selectedDates[0] &&
      date.compare(selectedDates[0]) >= 0;

    if (isEndFocused || selectingImplicitEnd) {
      // If user picked a date before current start while intending to set end,
      // interpret it as resetting the start (clearing end) for clarity.
      if (selectedDates[0] && date.compare(selectedDates[0]) < 0) {
        setSelectedDates[0](date);
        setSelectedDates[1]();
      } else {
        setSelectedDates[1](date);
      }
      return;
    }

    // We are selecting (or reselecting) the start date.
    // If a new start is after existing end, clear end.
    if (selectedDates[1] && date.compare(selectedDates[1]) > 0) {
      setSelectedDates[1]();
    }

    setSelectedDates[0](date);
    endReference.current?.focus();
  };
